<template>
	<div class="add-ons">
		<HeadingSVG imgName="select-add-ons" altText="Select Add Ons" />
		<ul class="add-ons-list">
			<li
				v-for="addon in addons" :key="addon.id"
				:class="['add-ons-list-item', { 'checked': !checkSelected(addon) }]"
			>
				<div class="priced-item-wrapper">
					<div class="checkbox">
						<input
							class="hidden-input"
							type="checkbox"
							:value="addon"
							:id="addon.title"
							v-model="selectedAddons"
						/>
						<span class="checkmark"></span>
					</div>
					<PricedItem :title="addon.title" :amount="addon.cost" />
				</div>
				<div class="add-on-description" v-html="addon.description" />
			</li>
		</ul>
		<div class="total-plan-wrapper">
			<div class="selected-add-ons">
				<PricedItem class="base-premium" title="Base Premium" :amount="plan.premium" />
				<transition-group name="fade" class="selected-add-ons-list" tag="ul">
					<li class="selected-add-on-list-item" v-for="addon in selectedAddons" :key="addon.id">
						<PricedItem v-bind="addon" :amount="addon.cost" />
					</li>
				</transition-group>
			</div>
			<div class="total-premium">
				<div class="total-premium-label">{{ ui.total_premium }}</div>
				<div class="total-amount">{{ totalPremium | currency }}</div>
			</div>
		</div>
		<div class="button-container">
			<MarqueeButton :action="back">
				{{ ui.back_button }} {{ insurance[plan.type].name }} Plans
			</MarqueeButton>
			<MarqueeButton :action="advance" :disabled="unaffordable">
				{{ ui.purchase_button }} ${{ totalPremium }}
			</MarqueeButton>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import HeadingSVG from '@/components/base/HeadingSVG';
import MarqueeButton from '@/components/base/MarqueeButton';
import PricedItem from "@/components/base/PricedItem";

export default {
	name: 'Addons',
	components: {
		HeadingSVG,
		MarqueeButton,
		PricedItem,
	},
	computed: {
		...mapState({
			plan: (state) => state.game.plans.selected,
			ui: (state) => state.global.api.data.ui.plans,
			insurance: (state) => state.global.api.data.insurance,
		}),
		...mapGetters({
			totalPremium: "game/plans/selected/totalCost",
			addons: "game/insurance/browsing/addons",
			cost: "game/plans/selected/totalCost",
			hasNextRound: "game/rounds/hasNextRound",
		}),
		selectedAddons: {
			get() {
				return this.plan.addons;
			},
			set(addons) {
				this.$store.dispatch("game/plans/selected/setAddons", addons);
			},
		},
		unaffordable() {
			return this.$store.state.game.balance.amount < this.cost;
		},
		insuranceName() {
			return this.insurance[this.plan.type].name;
		},
		roundName() {
			return this.hasNextRound ? this.insuranceName : 'Multi';
		},
		trackingCodes() {
			return this.selectedAddons.length > 0
				? this.selectedAddons.map((addon) => parseInt(addon.tracking_code)).reduce((a, b) => a + b)
				: 'NoAddons';
		},
	},
	methods: {
		checkSelected(addon) {
			return this.selectedAddons.find(({ id }) => id === addon.id);
		},
		back() {
			this.$router.push(`/game/shop/plans/${this.$store.state.game.insurance.viewing}`);
			this.$store.dispatch("sound/fx/play", { id: "button_generic" });
		},
		advance() {
			this.$store.dispatch("global/analytics/trackEvent",
				{ ec: `${this.roundName}Round`, el: `${this.insuranceName}Insurance`, ea: this.plan.title });
			this.$store.dispatch("global/analytics/trackEvent",
				{ ec: `${this.roundName}Round`, el: `${this.insuranceName}AddOns`, ea: this.trackingCodes });
			this.$router.push("/game/shop/plans/complete");
			this.$store.dispatch("sound/fx/play", { id: "button_generic" });
		},
	},
};
</script>

<style lang="scss" scoped>
	.add-ons {
		width: 80%;
		color: $gray-light;
		margin-bottom: $page-bottom-spacing;
	}

	.add-ons-list {
		width: 100%;
		padding-right: $checkbox-width;

		@include tablet { width: 80%; }
	}

	.add-ons-list-item {
		margin-bottom: 20px;

		&.checked {
			.priced-item { color: $alt-color-extra-light; }
			.add-on-description { color: $trivia-2; }
			.priced-item:after { border-color: $alt-color-extra-light; }
		}
	}

	.priced-item-wrapper {
		display: flex;
		flex-direction: row;
		align-items: center;
	}

	.checkbox { margin-right: 10px; }

	.checkbox,
	.hidden-input{
		cursor: pointer;
		display: block;
		position: relative;
		height: $checkbox-width;
		width: $checkbox-width;

		@include mobile {
			height: $checkbox-width-mobile;
			width: $checkbox-width-mobile;
		}
	}

	.hidden-input:checked ~ .checkmark {
		background: linear-gradient(90deg, $primary-color-light 0%, $primary-color-extra-light 100%);
		border-color: white;

		&:after { display: block; }
	}

	.checkmark {
		position: absolute;
		top: 0;
		left: 0;
		height: $checkbox-width;
		width: $checkbox-width;
		background-color: white;
		pointer-events: none;
		background: linear-gradient(90deg, $alt-color-vivid 0%, $alt-color-light 100%);
		border: solid $border-width-sm $alt-color-extra-light;

		@include mobile {
			height: $checkbox-width-mobile;
			width: $checkbox-width-mobile;
		}

		&:after {
			content: '';
			display: none;
			position: absolute;
			left: 50%;
			right: 0;
			bottom: 15%;
			margin: auto;
			width: 10px;
			height: 30px;
			border: solid white;
			border-top: $border-width-sm;
			border-left: $border-width-sm;
			transform: rotate(45deg);

			@include mobile { height: 20px; }
		}
	}

	.priced-item { text-transform: uppercase; }

	.add-on-description {
		font-size: $font-size-sm;
		padding-left: $checkbox-width;
		margin-left: 10px;
	}

	.total-plan-wrapper {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
    align-content: center;
		align-items: stretch;
		width: 100%;
		border: solid $border-width-md;
		border-image: linear-gradient(90deg, $alt-color-light, $alt-color-extra-light, $alt-color) 1;
		padding: calc(20px + 1rem);
		margin: 20px 0;

		@include tablet { flex-direction: row; }
	}

	.selected-add-ons {
		flex-grow: 1;
		padding: 0 0 20px 0;
		margin: 0 0 2.5px 0;
		border-bottom: solid $border-width-sm white;

		@include tablet {
			width: 0;
			padding: 0 20px 0 0;
			margin: 0 2.5px 0 0;
			border-right: solid $border-width-sm white;
			border-bottom: unset;
		}
	}

	.total-premium {
		text-transform: uppercase;
		color: white;
		flex-grow: 1;
		padding: 20px 0 0 0;
		margin: 2.5px 0 0 0;
		border-top: solid $border-width-sm white;

		@include tablet {
			width: 0;
			padding: 0 0 0 20px;
			margin: 0 0 0 2.5px;
			border-left: solid $border-width-sm white;
			border-top: unset;
		}
	}

	.total-amount {
		text-align: right;
		font-family: $heading-font;
		font-size: $heading-size-md;
		color: $primary-color-extra-light;
		line-height: 1.2;

		@include tablet {
			line-height: unset;
			text-align: center;
		}
	}

	.button-container {
		width: 100%;
		display: flex;
		flex-direction: column;
		justify-content: space-around;
		align-items: center;
		margin-top: 1rem;

		@include tablet {
			flex-direction: row;
		}
	}

	.marquee-btn:last-child {
		margin-bottom: 0;

		@include tablet { margin-bottom: 20px; }
	}

</style>
